/**
 * CSV import (added — see docs/BENCHMARKS.md).
 *
 * Dry run is the default and cannot be skipped by accident: the operator must see the
 * validation report before anything is written. Errors are reported per row, so one bad
 * ΑΦΜ on line 400 does not cost them lines 1–399.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Download, FileSpreadsheet, TriangleAlert, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader } from '@/components/common/page-header';
import { StatTile } from '@/components/common/stat-tile';
import { ApiRequestError, download } from '@/lib/api/client';
import { useImportClients } from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function ImportPage() {
  const { t, number } = useI18n();
  const runImport = useImportClients();

  const [file, setFile] = React.useState<File | null>(null);
  const [dryRun, setDryRun] = React.useState(true);
  const [dragging, setDragging] = React.useState(false);
  const result = runImport.data;

  const submit = async () => {
    if (!file) return;
    try {
      const outcome = await runImport.mutateAsync({ file, dryRun });
      if (outcome.dryRun) {
        toast.info(`${outcome.validRows}/${outcome.totalRows} ${t('import.validRows')}`);
      } else {
        toast.success(`${outcome.imported} ${t('import.imported')}`);
        setFile(null);
      }
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : t('error.generic'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('import.title')}
        description={t('import.subtitle')}
        actions={
          <Button
            variant="outline"
            onClick={() => void download('/imports/clients/template', 'clients-template.csv')}
          >
            <Download /> {t('import.template')}
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('action.import')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                const dropped = event.dataTransfer.files?.[0];
                if (dropped) setFile(dropped);
              }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center transition-colors',
                dragging ? 'border-primary bg-primary/[0.06]' : 'hover:border-primary/40 hover:bg-accent/40',
              )}
            >
              <FileSpreadsheet className="text-muted-foreground size-6" />
              <span className="text-sm font-medium">{file?.name ?? t('import.dropzone')}</span>
              <span className="text-muted-foreground text-xs">CSV · UTF-8 · ; ή ,</span>
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </label>

            <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <span className="text-sm">
                <span className="font-medium">{t('import.dryRun')}</span>
              </span>
              <Switch checked={dryRun} onCheckedChange={setDryRun} />
            </label>

            <Button
              className="w-full"
              disabled={!file}
              loading={runImport.isPending}
              onClick={() => void submit()}
            >
              <Upload /> {dryRun ? t('import.dryRun') : t('import.run')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('common.details')}</CardTitle>
            {result && (
              <Badge tone={result.dryRun ? 'info' : 'good'}>
                {result.dryRun ? t('import.dryRun') : t('import.imported')}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            {!result ? (
              <p className="text-muted-foreground py-10 text-center text-sm">{t('import.subtitle')}</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <StatTile label={t('import.totalRows')} value={number(result.totalRows)} />
                <StatTile label={t('import.validRows')} value={number(result.validRows)} />
                <StatTile
                  label={t('import.imported')}
                  value={number(result.imported)}
                  icon={CheckCircle2}
                />
                <StatTile
                  label={t('import.errors')}
                  value={number(result.errors.length)}
                  icon={TriangleAlert}
                  tone={result.errors.length > 0 ? 'critical' : 'default'}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {result && result.errors.length > 0 && (
        <Card className="mt-4 overflow-hidden">
          <CardHeader>
            <CardTitle>{t('import.errors')}</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t('import.row')}</TableHead>
                <TableHead className="w-40">{t('audit.entity')}</TableHead>
                <TableHead>{t('common.error')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.errors.map((error, index) => (
                <TableRow key={index}>
                  <TableCell className="py-2 tabular">{error.row}</TableCell>
                  <TableCell className="text-muted-foreground py-2 font-mono text-xs">
                    {error.field ?? '—'}
                  </TableCell>
                  <TableCell className="py-2 text-[13px]">{error.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
